import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, Navigate, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Badge, type TOPICS } from "../components/brand.tsx";
import { addPasskeyHere, devicesQuery, SignInDevices } from "../components/sign-in-devices.tsx";
import { AddTask, TaskList, tasksQuery } from "../components/tasks.tsx";
import { TimeSaved } from "../components/time-saved.tsx";
import { Today, todayQuery } from "../components/today.tsx";
import {
	Alert,
	Button,
	fieldClass,
	Page,
	Pill,
	primaryClass,
	quietClass,
	secondaryClass,
	tileClass,
} from "../components/ui.tsx";
import { YourDetails } from "../components/your-details.tsx";
import { api, type FileInfo, type Me } from "../lib/api.ts";
import { nextQuery } from "../lib/next.ts";
import { recents } from "../lib/recent.ts";
import { formatBytes, prepareImage, uploadWithProgress } from "../lib/resize.ts";

type Tile = {
	to:
		| "/tasks"
		| "/plan"
		| "/vacancies"
		| "/rates"
		| "/payroll"
		| "/reports"
		| "/settings/forms"
		| "/settings/team"
		| "/settings/ways"
		| "/candidates"
		| "/bookings"
		| "/timesheets"
		| "/clients"
		| "/imports"
		| "/platform"
		| "/compliance"
		| "/settings/documents"
		| "/settings/access";
	topic: keyof typeof TOPICS;
	title: string;
	text: string;
	permission?: string;
	platformOnly?: boolean;
};

const TILES: Tile[] = [
	{
		to: "/tasks",
		topic: "tasks",
		title: "Tasks",
		text: "Everything waiting for you, and your to-do list.",
		permission: "tasks.manage",
	},
	{ to: "/candidates", topic: "people", title: "Candidates", text: "Find, call and update the people you place." },
	{ to: "/clients", topic: "clients", title: "Clients", text: "Sites, contacts and who approves timesheets." },
	{
		to: "/bookings",
		topic: "planning",
		title: "Bookings",
		text: "Book shifts and fill them from the best people.",
		permission: "bookings.view",
	},

	{
		to: "/plan",
		topic: "planning",
		title: "Plan",
		text: "The week across every booking.",
		permission: "bookings.view",
	},
	{
		to: "/timesheets",
		topic: "time",
		title: "Timesheets",
		text: "Send people their link and see what's approved.",
		permission: "timesheets.view",
	},
	{
		to: "/vacancies",
		topic: "jobs",
		title: "Permanent jobs",
		text: "Vacancies, who's put forward and fees.",
		permission: "bookings.view",
	},
	{
		to: "/rates",
		topic: "finance",
		title: "Rate cards",
		text: "What clients pay and workers earn.",
		permission: "rate_cards.view",
	},
	{
		to: "/payroll",
		topic: "payroll",
		title: "Payroll",
		text: "Send approved hours to Evertime.",
		permission: "payroll.export",
	},
	{
		to: "/reports",
		topic: "growth",
		title: "Reports",
		text: "Hours, money and shifts filled.",
		permission: "reports.view",
	},
	{
		to: "/imports",
		topic: "upload",
		title: "Import a spreadsheet",
		text: "Bring in candidates and clients from CentricFlow or Excel.",
		permission: "agency.imports.run",
	},
	{
		to: "/compliance",
		topic: "trust",
		title: "Check documents",
		text: "Approve or reject documents candidates have added.",
		permission: "compliance.verify",
	},
	{
		to: "/settings/documents",
		topic: "planning",
		title: "Documents you need",
		text: "Choose which documents each job needs.",
		permission: "agency.document_types.manage",
	},
	{
		to: "/settings/forms",
		topic: "forms",
		title: "Forms",
		text: "Registration and forms candidates fill in.",
		permission: "agency.forms.manage",
	},
	{
		to: "/settings/team",
		topic: "team",
		title: "Your team",
		text: "Who can sign in and what they can do.",
		permission: "agency.users.manage",
	},
	{
		to: "/settings/ways",
		topic: "growth",
		title: "Ways of working",
		text: "Jobs the app can do for you, like reminders and repeat bookings.",
		permission: "agency.settings.manage",
	},
	{
		to: "/settings/access",
		topic: "trust",
		title: "Access and exports",
		text: "See SSG visits and who exported spreadsheets.",
		permission: "agency.users.manage",
	},
	{
		to: "/platform",
		topic: "trust",

		title: "All agencies",
		text: "SSG only: step into any agency to help.",
		platformOnly: true,
	},
];

export function Home({ me }: { me: Me }) {
	const navigate = useNavigate();
	const queue = useQuery({
		queryKey: ["compliance-queue"],
		queryFn: () => api<unknown[]>("/compliance/queue"),
		enabled: me.permissions.includes("compliance.verify"),
	});
	const today = new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" });
	return (
		<Page section={today} title={`Hello, ${me.user.name.split(" ")[0]}`} wide bare>
			<PasskeyNudge />
			{/* Laptops: two columns that line up. The day's work on the left; on the right, what
			    needs a check, then shortcuts. The sidebar replaces the tiles. */}
			<div className="lg:grid lg:grid-cols-[minmax(0,2fr)_minmax(18rem,1fr)] lg:items-start lg:gap-6">
				<div>
					{/* The most common job first: find someone. Enter goes straight to the results. */}
					<search>
						<form
							className="mb-5"
							onSubmit={(e) => {
								e.preventDefault();
								const q = String(new FormData(e.currentTarget).get("q") ?? "").trim();
								void navigate({ to: "/candidates", search: q ? { q } : {} });
							}}
						>
							<label htmlFor="home-search" className="block font-bold">
								Find a candidate
							</label>
							<div className="mt-2 flex gap-2">
								<input
									id="home-search"
									name="q"
									type="search"
									enterKeyHint="search"
									placeholder="Name, phone or postcode"
									className={fieldClass}
								/>
								{/* Secondary: the main button on Home is the work waiting for a person. */}
								<Button type="submit" variant="secondary" className="shrink-0">
									Find
								</Button>
							</div>
						</form>
					</search>
					<Today withTasks={me.permissions.includes("tasks.manage")} />
					<div className="mb-6 lg:hidden">
						<TimeSaved />
					</div>
					{me.permissions.includes("tasks.manage") ? <MyTasks me={me} /> : null}
				</div>
				<DeskPanel me={me} waiting={queue.data?.length ?? 0} />
			</div>
			<ul className="grid gap-4 sm:grid-cols-2 lg:hidden">
				{TILES.filter(
					(t) => (!t.permission || me.permissions.includes(t.permission)) && (!t.platformOnly || me.isPlatformAdmin),
				).map((t) => (
					<li key={t.to}>
						<Link to={t.to} className={`${tileClass} min-h-24`}>
							<Badge topic={t.topic} size={56} />
							<span>
								<span className="flex flex-wrap items-center gap-2 font-display text-lg leading-snug font-bold">
									{t.title}
									{t.to === "/compliance" && queue.data?.length ? (
										<Pill tone="warn">{queue.data.length} waiting</Pill>
									) : null}
								</span>
								<span className="mt-0.5 block text-sm text-ink-soft">{t.text}</span>
							</span>
						</Link>
					</li>
				))}
			</ul>
		</Page>
	);
}

/** The records you opened last, on this device. */
function JumpBackIn() {
	const [items] = useState(recents);
	if (!items.length) return null;
	return (
		<section aria-labelledby="recent-h" className="rounded-[var(--radius-card)] bg-card p-5">
			<h2 id="recent-h" className="font-display text-xl font-bold">
				Jump back in
			</h2>
			<ul className="mt-2 space-y-1">
				{items.slice(0, 5).map((r) => (
					<li key={r.href}>
						<Link
							to={r.href}
							className="flex min-h-11 items-center justify-between gap-2 rounded-lg px-2 hover:bg-sheet"
						>
							<span className="truncate font-semibold">{r.label}</span>
							<span className="shrink-0 text-sm text-ink-soft">{RECENT_KIND[r.kind]}</span>
						</Link>
					</li>
				))}
			</ul>
		</section>
	);
}
const RECENT_KIND = { candidate: "Candidate", client: "Client", booking: "Booking", vacancy: "Job" } as const;

/** Desktop only: what needs a check first, then the things people start most, then recent records. */
function DeskPanel({ me, waiting }: { me: Me; waiting: number }) {
	const can = (p: string) => me.permissions.includes(p);
	// Today's main button wins when it has one: something urgent, or work waiting in Tasks.
	const today = useQuery(todayQuery);
	const next = useQuery({ ...nextQuery, enabled: can("tasks.manage") });
	const urgent = Boolean(today.data?.items.length) || Boolean(next.data?.items.length);
	// Things to start look like things you can press: secondary buttons, full width.
	const linkClass = `${secondaryClass} w-full justify-start`;
	return (
		<aside aria-label="At a glance" className="hidden space-y-4 lg:block">
			{can("compliance.verify") ? (
				<section className="rounded-[var(--radius-card)] bg-card p-5">
					<h2 className="font-display text-xl font-bold">Documents to check</h2>
					{waiting > 0 ? (
						<>
							<p className="mt-1">
								<span className="font-display text-4xl font-bold tabular-nums">{waiting}</span>{" "}
								<span className="text-ink-soft">waiting for a check</span>
							</p>
							{/* The main button, unless Today has something more urgent. */}
							<Link to="/compliance" className={`${urgent ? secondaryClass : primaryClass} mt-3`}>
								Check them
							</Link>
						</>
					) : (
						<p className="mt-1 text-ink-soft">All checked. Nothing waiting.</p>
					)}
				</section>
			) : null}
			<section className="rounded-[var(--radius-card)] bg-card p-5">
				<h2 className="font-display text-xl font-bold">Start something</h2>
				<ul className="mt-3 space-y-2">
					{can("candidates.edit") ? (
						<li>
							<Link to="/candidates/new" className={linkClass}>
								Add a candidate
							</Link>
						</li>
					) : null}
					{can("bookings.edit") ? (
						<li>
							<Link to="/bookings/new" className={linkClass}>
								Book shifts
							</Link>
						</li>
					) : null}
					{can("clients.edit") ? (
						<li>
							<Link to="/clients/new" className={linkClass}>
								Add a client
							</Link>
						</li>
					) : null}
					{can("agency.imports.run") ? (
						<li>
							<Link to="/imports" className={linkClass}>
								Import a spreadsheet
							</Link>
						</li>
					) : null}
				</ul>
			</section>
			<JumpBackIn />
			{/* Good news, not a job: it sits under the things to do. */}
			<TimeSaved />
		</aside>
	);
}

const HOME_TASKS = 8;

/** The day's work, first thing: overdue, today, then what's coming up. */
function MyTasks({ me }: { me: Me }) {
	const [adding, setAdding] = useState(false);
	const all = useQuery(tasksQuery({}));
	const count = all.data?.length ?? 0;
	return (
		<section aria-labelledby="my-tasks" className="mb-6 rounded-[var(--radius-card)] bg-card p-5 sm:p-6">
			<div className="flex flex-wrap items-center justify-between gap-2">
				<h2 id="my-tasks" className="font-display text-xl font-bold">
					Your tasks
				</h2>
				{!adding ? (
					<Button variant="quiet" onClick={() => setAdding(true)} className="-mr-2">
						Add a task
					</Button>
				) : null}
			</div>
			{adding ? <AddTask meId={me.user.id} onDone={() => setAdding(false)} /> : null}
			<div className="mt-2">
				<TaskList
					params={{}}
					grouped
					max={HOME_TASKS}
					empty="Nothing to do"
					emptyHint="Add a task to remind yourself to call someone back."
				/>
			</div>
			{count > HOME_TASKS ? (
				<Link to="/tasks" className={`mt-2 ${quietClass} -ml-2`}>
					See all {count} tasks
				</Link>
			) : (
				<Link to="/tasks" className={`mt-2 ${quietClass} -ml-2`}>
					Done tasks and the team's tasks
				</Link>
			)}
		</section>
	);
}

export function ChooseAgency({ me }: { me: Me }) {
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const choose = useMutation({
		mutationFn: (agencyId: string) => api("/session/agency", { body: { agencyId } }),
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ["me"] });
			await navigate({ to: "/" });
		},
	});
	// One agency: there's nothing to choose, so open it.
	const only = me.agencies.length === 1 ? me.agencies[0] : undefined;
	useEffect(() => {
		if (only && choose.isIdle) choose.mutate(only.id);
	}, [only, choose]);
	if (only)
		return (
			<Page section="Getting started" title={`Opening ${only.name}`}>
				<p aria-live="polite">{choose.isError ? "That didn't work. Refresh the page to try again." : "One moment"}</p>
			</Page>
		);
	// SSG staff with no agency of their own have one place to go: All agencies.
	if (me.agencies.length === 0 && me.isPlatformAdmin) return <Navigate to="/platform" replace />;
	if (me.agencies.length === 0) {
		return (
			<Page
				section="Getting started"
				title={me.stopped ? "Your agency isn't available" : "No agency yet"}
				intro={
					me.stopped ?? "Your account is not linked to an agency. Ask your agency admin to add you, then sign in again."
				}
			>
				<span />
			</Page>
		);
	}
	return (
		<Page
			section="Getting started"
			title="Which agency are you working for?"
			intro="You can switch at any time from the bar at the top."
		>
			{me.stopped ? (
				<div className="mb-4">
					<Alert tone="note">{me.stopped}</Alert>
				</div>
			) : null}
			<ul className="space-y-3">
				{me.agencies.map((a) => (
					<li key={a.id}>
						<button
							type="button"
							onClick={() => choose.mutate(a.id)}
							disabled={choose.isPending}
							className={`${secondaryClass} w-full justify-start text-lg`}
						>
							{a.name}
						</button>
					</li>
				))}
			</ul>
		</Page>
	);
}

type Stage =
	| { step: "idle" }
	| { step: "uploading"; progress: number }
	| { step: "processing"; fileId: string }
	| { step: "failed"; message: string };

export function Upload() {
	const [stage, setStage] = useState<Stage>({ step: "idle" });
	const fileId = stage.step === "processing" ? stage.fileId : null;

	const file = useQuery({
		queryKey: ["file", fileId],
		queryFn: () => api<FileInfo>(`/files/${fileId}`),
		enabled: fileId !== null,
		refetchInterval: (q) => (q.state.data?.status === "pending_scan" ? 1000 : false),
	});

	async function onPick(picked: File | undefined) {
		if (!picked) return;
		try {
			setStage({ step: "uploading", progress: 0 });
			const prepared = await prepareImage(picked);
			const created = await api<{ id: string; upload: { url: string; fields: Record<string, string> } }>("/files", {
				body: { name: prepared.name, contentType: prepared.type || "image/heic", size: prepared.size },
			});
			await uploadWithProgress(created.upload, prepared, (progress) => setStage({ step: "uploading", progress }));
			await api(`/files/${created.id}/complete`, { body: {} });
			setStage({ step: "processing", fileId: created.id });
		} catch (err) {
			setStage({ step: "failed", message: err instanceof Error ? err.message : "The upload did not finish." });
		}
	}

	const info = file.data;
	return (
		<Page
			title="Upload a photo"
			intro="Take a photo or choose one. iPhone photos are fine; we convert them and remove location data."
		>
			{stage.step === "failed" ? <Alert>{stage.message}</Alert> : null}
			{info?.status === "infected" ? <Alert>This file failed the virus check and has been deleted.</Alert> : null}

			<label
				className={`${primaryClass} w-full cursor-pointer focus-within:outline-3 focus-within:outline-offset-2 focus-within:outline-[var(--color-focus)]`}
			>
				{stage.step === "idle" || stage.step === "failed" || info?.status === "clean"
					? "Choose or take a photo"
					: "Uploading"}
				<input
					type="file"
					accept="image/*,.heic,.heif"
					className="sr-only"
					onChange={(e) => onPick(e.currentTarget.files?.[0])}
					disabled={stage.step === "uploading" || info?.status === "pending_scan"}
				/>
			</label>

			<div aria-live="polite" className="mt-6">
				{stage.step === "uploading" ? (
					<p>
						Uploading <span className="tabular-nums">{Math.round(stage.progress * 100)}%</span>
					</p>
				) : null}
				{stage.step === "processing" && (!info || info.status === "pending_scan") ? (
					<p>Checking for viruses and converting the photo</p>
				) : null}
				{info?.status === "clean" && info.urls.webp ? (
					<figure>
						<img
							src={info.urls.webp}
							alt={`Uploaded: ${info.name}`}
							width={info.variants.webp?.width}
							height={info.variants.webp?.height}
							className="h-auto w-full rounded-xl"
						/>
						<figcaption className="mt-3 flex items-center gap-3 text-sm text-ink-soft">
							{info.urls.thumb ? (
								<img src={info.urls.thumb} alt="" width={48} height={64} className="h-16 w-auto rounded-lg" />
							) : null}
							<span>
								<span className="font-semibold text-clear">Ready</span>
								<br />
								WebP,{" "}
								<span className="tabular-nums">
									{info.variants.webp?.width}×{info.variants.webp?.height}
								</span>
								, <span className="tabular-nums">{formatBytes(info.variants.webp?.bytes ?? 0)}</span>
							</span>
						</figcaption>
					</figure>
				) : null}
			</div>
		</Page>
	);
}

export function Account({ me }: { me: Me }) {
	return (
		<Page
			section="Account"
			title="Your account"
			intro="Your name, the email you sign in with, and how you sign in."
			bare
		>
			<div className="space-y-4">
				<YourDetails me={me} />
				<SignInDevices />
			</div>
		</Page>
	);
}

/** Straight after signing in by email: one tap to make next time quicker. */
function PasskeyNudge() {
	const check = useQuery({ ...devicesQuery, staleTime: 300_000 });
	const [hidden, setHidden] = useState(() => {
		try {
			return localStorage.getItem("passkey-nudge-hidden") === "1";
		} catch {
			return false;
		}
	});
	const [done, setDone] = useState(false);
	if (hidden || !check.data || check.data.passkeys.length > 0)
		return done ? <Alert tone="clear">Passkey added. Next time, tap the email box and choose it.</Alert> : null;
	return (
		<aside
			aria-label="Sign in faster"
			className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-[var(--radius-card)] bg-card p-4"
		>
			<p className="max-w-xl">
				<span className="font-semibold">Sign in faster next time.</span> Add a passkey and use Face ID, Touch ID or your
				fingerprint instead of waiting for an email.
			</p>
			<span className="flex flex-wrap gap-3">
				<Button
					variant="secondary"
					onClick={async () => {
						if (await addPasskeyHere()) {
							setDone(true);
							void check.refetch();
						}
					}}
				>
					Add a passkey
				</Button>
				<Button
					variant="quiet"
					onClick={() => {
						try {
							localStorage.setItem("passkey-nudge-hidden", "1");
						} catch {}
						setHidden(true);
					}}
				>
					Not now
				</Button>
			</span>
		</aside>
	);
}
